import type { AutocompleteChangeReason } from "@mui/material/Autocomplete";

/**
 * Shared Autocomplete interaction rules for catalog item pickers.
 *
 * - autoHighlight: keyboard navigation highlights a match (does not commit)
 * - no autoSelect: autoSelect commits the highlighted option on blur — that was
 *   selecting the wrong item as soon as the list opened / lost focus
 * - no autoComplete: avoids filling the input as if an item were already chosen
 * - clearOnBlur: discard partial typed text that was never confirmed
 *
 * Commit only on click or Enter of a highlighted option (MUI reason: selectOption).
 *
 * Keep this as a plain object (not AutocompleteProps<...>) so spreading it does
 * not force option types to `unknown` on consumers.
 */
export const catalogItemAutocompleteInteractionProps = {
	openOnFocus: true,
	autoHighlight: true,
	autoSelect: false,
	autoComplete: false,
	clearOnBlur: true,
	blurOnSelect: true,
	selectOnFocus: true,
};

type CatalogOption = { id?: number | string | null } | null;

/**
 * Apply catalog selection only for intentional user commits.
 * Ignores blur / createOption / removeOption noise.
 */
export function handleCatalogItemAutocompleteChange(
	reason: AutocompleteChangeReason,
	newValue: CatalogOption,
	handlers: {
		onSelect: (id: number) => void;
		onClear?: () => void;
	},
): void {
	if (reason === "clear") {
		handlers.onClear?.();
		return;
	}

	if (reason !== "selectOption") {
		return;
	}

	const id = Number(newValue?.id);
	if (Number.isFinite(id) && id > 0) {
		handlers.onSelect(id);
	}
}

/** Stable option equality when catalog ids may be number or string. */
export function isCatalogItemOptionEqualToValue<T extends { id?: number | string | null }>(
	option: T,
	value: T,
): boolean {
	return Number(option?.id) === Number(value?.id);
}
